import type { z } from 'zod';
import { ApiError, type ProblemDetails } from '../../shared/api/client';

/**
 * A POST to a session or sign-in endpoint (`/auth/…`, `/api/v1/me/…`, the setup link): these return
 * plain JSON, not the `{ data }` record envelope, and a 401 here means "not correct", not "your
 * session ended". The CSRF token comes from `/auth/methods` before sign-in, or `/api/v1/me` after.
 */
export async function postAuth<S extends z.ZodType>(
  path: string,
  body: unknown,
  schema: S | null,
  csrfToken: string,
): Promise<z.infer<S> | undefined> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify(body),
  }).catch(() => null);
  if (res === null) throw new ApiError('The server could not be reached. Try again.', 0, null);
  if (!res.ok) {
    const problem = await res.json().then(
      (body: unknown) => body as ProblemDetails,
      () => null,
    );
    throw new ApiError(messageFor(res.status, problem), res.status, problem);
  }
  if (schema === null || res.status === 204) return undefined;
  return schema.parse(await res.json());
}

function messageFor(status: number, problem: ProblemDetails | null): string {
  if (status === 429) return 'Too many attempts. Wait a minute and try again.';
  if (status === 403) return 'The form has expired. Reload the page and try again.';
  if (status === 503 && problem?.code === 'configuration_missing') {
    return 'Sign-in is not configured yet. An administrator must set the sign-in policy.';
  }
  if (status >= 500) return 'This is unavailable right now. Try again shortly.';
  const detail = problem?.errors?.[0]?.message ?? problem?.detail;
  return detail !== undefined && detail !== '' ? detail : `The request failed (${String(status)}).`;
}
